import '../styles/Questions.css'
import { coupleQuestionsSession } from "../config/AIModal";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../config/firebase";
import { getFirestore, doc, onSnapshot } from "firebase/firestore";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Dialog } from "primereact/dialog";
import axios from 'axios';
import { Hearts } from 'react-loader-spinner';

const Questions = () => {
    const [loading, setLoading] = useState(false);
    const [categoryLoading, setCategoryLoading] = useState('');
    const [loadingType, setLoadingType] = useState(''); // 'creating' or 'regenerating'
    const [questions, setQuestions] = useState({ standard: {} });
    const [partnerQuestions, setPartnerQuestions] = useState({ standard: {} });
    const [answeredQuestions, setAnsweredQuestions] = useState({ user: {}, partner: {} });
    const [activeCategory, setActiveCategory] = useState('Romantic');
    const [hasPartner, setHasPartner] = useState(false);
    const [partnerId, setPartnerId] = useState(null);
    const [partnerName, setPartnerName] = useState('Partner');
    const [currentUserName, setCurrentUserName] = useState('You');
    const [userScore, setUserScore] = useState(0);
    const [partnerScore, setPartnerScore] = useState(0);
    const availableCategories = [
        'Romantic', 'Spicy', 'General', 'Funny',
        'Family', 'Relationship Reinforcement', 'Deep Connection', 'Mix'
    ];
    const [animating, setAnimating] = useState(false);
    const [animationCategory, setAnimationCategory] = useState('');
    const navigate = useNavigate();
    const [remainingRegenerations, setRemainingRegenerations] = useState(0);
    const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
    const [showNoRegenerationsDialog, setShowNoRegenerationsDialog] = useState(false);
    const [currentCategory, setCurrentCategory] = useState('');

    const getCategoryRegenerationCount = () => {
        // Return server-provided remaining regenerations
        const count = Math.max(0, remainingRegenerations);
        console.log("getCategoryRegenerationCount called, remainingRegenerations:", remainingRegenerations, "returning:", count);
        return count;
    };

    // Get all available categories from both user and partner
    const getAllAvailableCategories = () => {
        const userCategories = questions.standard ? Object.keys(questions.standard) : [];
        const partnerCategories = partnerQuestions.standard ? Object.keys(partnerQuestions.standard) : [];

        // Combine and deduplicate categories
        const allCategoryKeys = [...new Set([...userCategories, ...partnerCategories])];

        // Convert back to display names
        const availableCategoriesFromData = allCategoryKeys.map(categoryKey => {
            // Convert from formatted key back to display name
            const displayName = availableCategories.find(cat => 
                formatCategoryKey(cat) === categoryKey
            );
            return displayName || categoryKey;
        }).filter(Boolean);

        // Combine with default categories, prioritizing ones with data
        const categoriesWithData = availableCategoriesFromData.filter(cat => 
            availableCategories.includes(cat)
        );
        const categoriesWithoutData = availableCategories.filter(cat => 
            !categoriesWithData.includes(cat)
        );

        return [...categoriesWithData, ...categoriesWithoutData];
    };

    // Check if a category has questions from either user or partner
    const categoryHasQuestions = (category) => {
        const categoryKey = formatCategoryKey(category);
        const userHasQuestions = !!questions.standard?.[categoryKey];
        const partnerHasQuestions = !!partnerQuestions.standard?.[categoryKey];
        return userHasQuestions || partnerHasQuestions;
    };

    // Helper function to format category key consistently
    const formatCategoryKey = (category) => {
        return category.toLowerCase().replace(/\s+/g, '');
    };

    // Get random items from an array
    const getRandomItems = (array, count) => {
        if (!array || !Array.isArray(array)) return [];
        const shuffled = [...array].sort(() => 0.5 - Math.random());
        return shuffled.slice(0, Math.min(count, shuffled.length));
    };

    // Get questions for display (prioritize user questions, fallback to partner)
    const getQuestionsForCategory = (category) => {
        const categoryKey = formatCategoryKey(category);
        const userQuestions = questions.standard?.[categoryKey];
        const partnerQuestionsForCategory = partnerQuestions.standard?.[categoryKey];

        // Prefer user questions if available, otherwise use partner questions
        return userQuestions || partnerQuestionsForCategory || null;
    };

    // Setup Firestore real-time listeners for AIQuestions documents
    useEffect(() => {
        const db = getFirestore();
        // References to store unsubscribe functions for snapshots
        const unsubscribers = [];

        // Initial data fetch to get user and partner info
        const fetchInitialData = async () => {
            try {
                const currentUser = auth.currentUser;
                if (!currentUser) {
                    toast.error("You must be logged in to view questions");
                    navigate("/login");
                    return;
                }

                setLoading(true);

                // Fetch initial data using the REST API to get user and partner info
                const response = await axios.get(`https://api.frazza.online/questions?userId=${currentUser.uid}`);

                if (response.data) {
                    const data = response.data;

                    // Set user data
                    setCurrentUserName(data.user.name || "You");
                    setUserScore(data.user.score || 0);

                    // Set partner data if exists
                    if (data.partner) {
                        setHasPartner(true);
                        setPartnerId(data.partner.id);
                        setPartnerName(data.partner.name || "Partner");
                        setPartnerScore(data.partner.score || 0);

                        // Now that we have the partner ID, set up the Firestore listeners
                        setupFirestoreListeners(currentUser.uid, data.partner.id);
                    } else {
                        // No partner, just set up listener for current user
                        setupFirestoreListeners(currentUser.uid, null);
                    }

                    // Get remaining regenerations
                    try {
                        const regenResponse = await axios.get(`https://api.frazza.online/questions/remaining-regenerations?userId=${currentUser.uid}`);
                        if (regenResponse.data && regenResponse.data.success) {
                            console.log("Initial remainingGeneration from API:", regenResponse.data.remainingGeneration);
                            setRemainingRegenerations(regenResponse.data.remainingGeneration || 0);

                            if (regenResponse.data.wasReset) {
                                toast.info("Your question regenerations have been reset to 10! This happens once a month.", {
                                    autoClose: 5000
                                });
                            }
                        }
                    } catch (regenError) {
                        console.error("Error fetching regenerations:", regenError);
                    }
                }

                setLoading(false);

            } catch (error) {
                console.error("Error fetching initial data:", error);
                toast.error(`Failed to load questions: ${error.response?.data?.error || error.message}`);
                setLoading(false);
            }
        };

        // Set up Firestore listeners for real-time updates
        const setupFirestoreListeners = (userId, partnerId) => {
            // Clean up any existing listeners
            unsubscribers.forEach(unsub => unsub());
            unsubscribers.length = 0;

            // Set up listener for QuestionsSession document to get real-time remainingGeneration updates
            if (partnerId) {
                const userIds = [userId, partnerId].sort();
                const sessionId = `${userIds[0]}_${userIds[1]}`;
                console.log("Setting up QuestionsSession listener with sessionId:", sessionId);

                try {
                    const sessionListener = onSnapshot(
                        doc(db, "QuestionsSession", sessionId),
                        (docSnapshot) => {
                            if (docSnapshot.exists()) {
                                const sessionData = docSnapshot.data();
                                console.log("QuestionsSession updated:", sessionData);

                                // Update remainingGeneration from QuestionsSession
                                if (sessionData.remainingGeneration !== undefined) {
                                    console.log("Updating remainingRegenerations to:", sessionData.remainingGeneration);
                                    setRemainingRegenerations(sessionData.remainingGeneration);
                                }
                            } else {
                                console.log("QuestionsSession document does not exist");
                            }
                        },
                        (error) => {
                            console.error("Error in QuestionsSession listener:", error);
                        }
                    );
                    unsubscribers.push(sessionListener);
                    console.log("QuestionsSession listener set up successfully");
                } catch (error) {
                    console.error("Error setting up QuestionsSession listener:", error);
                }
            }

            // Set up listener for current user's UserInformation document to get real-time score updates
            const userInfoListener = onSnapshot(
                doc(db, "UserInformation", userId),
                (docSnapshot) => {
                    if (docSnapshot.exists()) {
                        const userInfo = docSnapshot.data();
                        console.log("User information updated:", userInfo);

                        // Update user score from UserInformation
                        if (userInfo.score !== undefined) {
                            setUserScore(userInfo.score);
                        }
                    }
                },
                (error) => {
                    console.error("Error in user information listener:", error);
                }
            );
            unsubscribers.push(userInfoListener);

            // Set up listener for current user's questions
            const userListener = onSnapshot(
                doc(db, "AIQuestions", userId),
                (docSnapshot) => {
                    if (docSnapshot.exists()) {
                        const userData = docSnapshot.data();
                        console.log("User questions updated:", userData);

                        // Update user questions
                        if (userData.questions && userData.questions.standard) {
                            setQuestions(prev => ({
                                ...prev,
                                standard: userData.questions.standard
                            }));
                        }

                        // Update user answered questions
                        if (userData.answeredQuestions) {
                            setAnsweredQuestions(prev => ({
                                ...prev,
                                user: userData.answeredQuestions
                            }));
                        }

                        // We still keep this as a fallback, but the UserInformation listener should be the primary source
                        if (userData.score !== undefined) {
                            setUserScore(userData.score);
                        }
                    }
                },
                (error) => {
                    console.error("Error in user questions listener:", error);
                }
            );
            unsubscribers.push(userListener);

            // Set up listener for partner's questions if partner exists
            if (partnerId) {
                // Set up listener for partner's UserInformation document to get real-time score updates
                const partnerInfoListener = onSnapshot(
                    doc(db, "UserInformation", partnerId),
                    (docSnapshot) => {
                        if (docSnapshot.exists()) {
                            const partnerInfo = docSnapshot.data();
                            console.log("Partner information updated:", partnerInfo);

                            // Update partner score from UserInformation
                            if (partnerInfo.score !== undefined) {
                                setPartnerScore(partnerInfo.score);
                            }
                        }
                    },
                    (error) => {
                        console.error("Error in partner information listener:", error);
                    }
                );
                unsubscribers.push(partnerInfoListener);

                // Set up listener for partner's questions
                const partnerListener = onSnapshot(
                    doc(db, "AIQuestions", partnerId),
                    (docSnapshot) => {
                        if (docSnapshot.exists()) {
                            const partnerData = docSnapshot.data();
                            console.log("Partner questions updated:", partnerData);

                            // Update partner questions
                            if (partnerData.questions && partnerData.questions.standard) {
                                setPartnerQuestions(prev => ({
                                    ...prev,
                                    standard: partnerData.questions.standard
                                }));
                            }

                            // Update partner answered questions
                            if (partnerData.answeredQuestions) {
                                setAnsweredQuestions(prev => ({
                                    ...prev,
                                    partner: partnerData.answeredQuestions
                                }));
                            }

                            // We still keep this as a fallback, but the UserInformation listener should be the primary source
                            if (partnerData.score !== undefined) {
                                setPartnerScore(partnerData.score);
                            }
                        }
                    },
                    (error) => {
                        console.error("Error in partner questions listener:", error);
                    }
                );
                unsubscribers.push(partnerListener);
            }
        };

        const unsubscribeAuth = auth.onAuthStateChanged((currentUser) => {
            if (currentUser) {
                fetchInitialData();
            } else {
                // Clean up listeners
                unsubscribers.forEach(unsub => unsub());
                unsubscribers.length = 0;

                // Clear data when logged out
                setQuestions({ standard: {} });
                setPartnerQuestions({ standard: {} });
                setAnsweredQuestions({ user: {}, partner: {} });
                setHasPartner(false);
                setPartnerId(null);
                setLoading(false);
            }
        });

        return () => {
            unsubscribeAuth();
            // Clean up all Firestore listeners
            unsubscribers.forEach(unsub => unsub());
        };
    }, [navigate]);



    // Generate category using REST API
    const handleGenerateCategory = async (category, skipDecrementCounter = false) => {
        // Check if user has regenerations left
        if (remainingRegenerations <= 0) {
            toast.warning("No more regeneration credits left. Please try again in 30 days.");
            return;
        }

        setCategoryLoading(category);
        setLoadingType('creating');

        try {
            const currentUser = auth.currentUser;
            if (!currentUser) {
                throw new Error("User not authenticated");
            }

            const formattedCategory = formatCategoryKey(category);
            let categoryData = {};

            if (category === 'Mix') {
                // Mix category logic - combine questions from both user and partner
                const userQuestionSets = Object.entries(questions.standard || {})
                    .filter(([key]) => key !== 'mix');
                const partnerQuestionSets = Object.entries(partnerQuestions.standard || {})
                    .filter(([key]) => key !== 'mix');

                // Combine all available question sets
                const allQuestionSets = [...userQuestionSets, ...partnerQuestionSets];

                // Remove duplicates by category key
                const uniqueQuestionSets = allQuestionSets.reduce((acc, [key, questions]) => {
                    if (!acc[key]) {
                        acc[key] = questions;
                    }
                    return acc;
                }, {});

                const availableQuestionSets = Object.entries(uniqueQuestionSets);

                if (availableQuestionSets.length < 3) {
                    setCategoryLoading('');
                    return toast.warning("At least 3 other categories need to exist (from you or your partner) before creating a mix");
                }

                const mixQuestions = [];
                availableQuestionSets.forEach(([cat, questionList]) => {
                    const numberOfQuestionsToTake = Math.min(Math.floor(15 / availableQuestionSets.length), 3);
                    const selectedQuestions = getRandomItems(questionList, numberOfQuestionsToTake);
                    mixQuestions.push(...selectedQuestions);
                });

                categoryData = { [formattedCategory]: mixQuestions.slice(0, 15) };
            } else {
                // Generate questions for standard mode
                const prompt = `Generate 15 thoughtful and engaging questions for couples in the "${category}" category. Create a balanced mix of fun and meaningful questions for couples to answer together. If Spicy, make those questions extremely dirty and spicy please. Return the response in JSON format with a single key "${formattedCategory}" and an array of questions as values.`;

                const result = await coupleQuestionsSession.sendMessage(prompt);
                const responseText = result?.response?.text();

                if (!responseText) {
                    throw new Error("Empty response from AI");
                }

                try {
                    categoryData = JSON.parse(responseText);
                } catch (parseError) {
                    console.error("Error parsing AI response:", parseError);
                    throw new Error("Failed to parse AI response");
                }
            }

            // Generate questions using the REST API
            const response = await axios.post('https://api.frazza.online/questions/generate', {
                userId: currentUser.uid,
                category: category,
                gameMode: 'standard',
                questions: categoryData,
                skipDecrementCounter: skipDecrementCounter
            });

            if (response.data && response.data.success) {
                // Update local state with the new questions
                setQuestions(prev => ({
                    ...prev,
                    standard: {
                        ...prev.standard,
                        ...categoryData
                    }
                }));

                // Update remaining regenerations
                if (response.data.remainingGeneration !== undefined) {
                    console.log("Updating remainingRegenerations after generation to:", response.data.remainingGeneration);
                    setRemainingRegenerations(response.data.remainingGeneration);
                }

                toast.success(`Questions generated for ${category}`);
            } else {
                throw new Error(response.data?.error || "Failed to generate questions");
            }
        } catch (error) {
            console.error(`Error generating ${category} questions:`, error);
            if (error.response?.status === 403) {
                toast.error("No more regeneration credits left. Please try again in 30 days.");
            } else {
                toast.error(`Failed to generate ${category} questions: ${error.response?.data?.error || error.message}`);
            }
        } finally {
            setCategoryLoading('');
            setLoadingType('');
        }
    };




    // Function to perform the actual regeneration using REST API
    const performRegeneration = async (category) => {
        console.log(`Starting regeneration for category: ${category}`);
        console.log("Current remainingRegenerations before regeneration:", remainingRegenerations);

        setCategoryLoading(category);
        setLoadingType('regenerating');
        const currentUser = auth.currentUser;
        if (!currentUser) {
            console.log("User not logged in, cannot regenerate");
            toast.error("You must be logged in to regenerate questions");
            return;
        }

        try {
            console.log(`Deleting and regenerating category: ${category}`);
            const formattedCategory = formatCategoryKey(category);

            // Delete existing questions using REST API
            console.log("Deleting existing questions with skipDecrementCounter=true");
            const deleteResponse = await axios.delete('https://api.frazza.online/questions/category', {
                params: {
                    userId: currentUser.uid,
                    category: formattedCategory,
                    gameMode: 'standard',
                    skipDecrementCounter: 'true' // Skip decrement since we'll decrement during generation
                }
            });
            console.log("Delete response:", deleteResponse.data);

            // Generate new questions (skipDecrementCounter as false to ensure counter is decremented)
            console.log("Generating new questions with skipDecrementCounter=false");
            await handleGenerateCategory(category, false);

        } catch (error) {
            console.error(`Error during regeneration process:`, error);
            if (error.response?.status === 403) {
                toast.error("No more regeneration credits left. Please try again in 30 days.");
            } else {
                toast.error(`Failed to regenerate ${category} questions: ${error.response?.data?.error || error.message}`);
            }
            throw error;
        }
    };



    // Function to handle confirmation from the regenerate dialog
    const confirmRegeneration = async () => {
        console.log("Regeneration confirmed for category:", currentCategory);
        console.log("Current remainingRegenerations before confirmation:", remainingRegenerations);
        setShowRegenerateDialog(false);

        try {
            console.log("Calling performRegeneration");
            await performRegeneration(currentCategory);
            console.log("performRegeneration completed successfully");
            console.log("Current remainingRegenerations after regeneration:", remainingRegenerations);
        } catch (error) {
            console.error(`Error regenerating ${currentCategory} questions:`, error);
            toast.error(`Failed to regenerate ${currentCategory} questions: ${error.message}`);
        } finally {
            console.log("Regeneration process completed, cleaning up UI state");
            setCategoryLoading('');
            setLoadingType('');
        }
    };

    const handleRegenerateCategory = async (category) => {
        try {
            console.log("Regenerate button clicked for category:", category);
            console.log("Current remainingRegenerations:", remainingRegenerations);
            console.log("Result of getCategoryRegenerationCount():", getCategoryRegenerationCount());

            // Check regeneration count first
            if (getCategoryRegenerationCount() <= 0) {
                console.log("No regenerations left, showing dialog");
                setCurrentCategory(category);
                setShowNoRegenerationsDialog(true);
                toast.warning("No more regeneration credits left. Please try again in 30 days.");
                return;
            }

            // Show custom dialog for confirmation
            console.log("Showing regenerate confirmation dialog");
            setCurrentCategory(category);
            setShowRegenerateDialog(true);
        } catch (error) {
            console.error(`Error regenerating ${category} questions:`, error);
            toast.error(`Failed to regenerate ${category} questions: ${error.message}`);
        } finally {
            setCategoryLoading('');
            setLoadingType('');
        }
    };

    // Toggle answered question status using REST API
    const toggleAnswered = async (category, index, userId) => {
        // Create consistent question ID
        const questionId = `standard-${category}-${index}`;

        // Optimistically update UI first for immediate feedback
        setAnsweredQuestions(prev => {
            const isCurrentUser = userId === auth.currentUser?.uid;
            const userKey = isCurrentUser ? 'user' : 'partner';
            const newValue = !prev[userKey][questionId];

            return {
                ...prev,
                [userKey]: {
                    ...prev[userKey],
                    [questionId]: newValue
                }
            };
        });

        // Apply animation class to score for visual feedback
        const scoreElement = document.querySelector(userId === auth.currentUser?.uid ?
            '.user-score-badge span' : '.partner-score-badge span');
        if (scoreElement) {
            scoreElement.classList.add('score-changed');
            setTimeout(() => scoreElement.classList.remove('score-changed'), 800);
        }

        try {
            // Toggle question using REST API
            const response = await axios.post('https://api.frazza.online/questions/toggle-answered', {
                userId: userId,
                category: category,
                index: index,
                gameMode: 'standard'
            });

            if (response.data && response.data.success) {
                // Update local state with the correct data from server
                setAnsweredQuestions(prev => ({
                    ...prev,
                    [userId === auth.currentUser?.uid ? 'user' : 'partner']: response.data.answeredQuestions
                }));

                // Update local score
                if (userId === auth.currentUser?.uid) {
                    setUserScore(response.data.score);
                } else {
                    setPartnerScore(response.data.score);
                }

                console.log("Question toggled successfully via REST API");
            } else {
                throw new Error(response.data?.error || "Failed to toggle question");
            }

        } catch (error) {
            console.error("Error toggling question:", error);
            toast.error(`Failed to update question status: ${error.response?.data?.error || error.message}`);

            // Revert the optimistic update if API call fails
            setAnsweredQuestions(prev => {
                const isCurrentUser = userId === auth.currentUser?.uid;
                const userKey = isCurrentUser ? 'user' : 'partner';

                return {
                    ...prev,
                    [userKey]: {
                        ...prev[userKey],
                        [questionId]: !prev[userKey][questionId] // Revert to previous state
                    }
                };
            });
        }
    };

    // Update handleCategoryClick function
    const handleCategoryClick = (category) => {
        setActiveCategory(category);
        const categoryKey = formatCategoryKey(category);

        // Generate questions if they don't exist anywhere and we have regenerations
        if (!categoryHasQuestions(category) && remainingRegenerations > 0) {
            handleGenerateCategory(category);
        }
    };

    // Update handleRandomCategory function
    const handleRandomCategory = () => {
        if (animating) return;

        setAnimating(true);
        let animationCounter = 0;
        const allCategories = getAllAvailableCategories();

        const animationInterval = setInterval(() => {
            const randomIndex = Math.floor(Math.random() * allCategories.length);
            setAnimationCategory(allCategories[randomIndex]);
            animationCounter++;

            if (animationCounter > 15) {
                clearInterval(animationInterval);
                const finalRandomIndex = Math.floor(Math.random() * allCategories.length);
                const selectedCategory = allCategories[finalRandomIndex];

                setAnimationCategory('');
                setAnimating(false);
                setActiveCategory(selectedCategory);

                if (!categoryHasQuestions(selectedCategory) && remainingRegenerations > 0) {
                    handleGenerateCategory(selectedCategory);
                }
            }
        }, 150);
    };

    // Render a question card with competitive indicators
    const renderQuestionCard = (question, index) => {
        const categoryKey = formatCategoryKey(activeCategory);
        const questionId = `standard-${categoryKey}-${index}`;
        const isUserAnswered = answeredQuestions.user[questionId];
        const isPartnerAnswered = answeredQuestions.partner[questionId];

        // Determine competitive status for card styling
        const userAhead = isUserAnswered && !isPartnerAnswered;
        const partnerAhead = !isUserAnswered && isPartnerAnswered;
        const bothAnswered = isUserAnswered && isPartnerAnswered;

        let cardClassName = "question-card";
        if (bothAnswered) cardClassName += " completed";
        else if (userAhead) cardClassName += " you-ahead";
        else if (partnerAhead) cardClassName += " partner-ahead";

        return (
            <Card key={index} className={cardClassName} pt={{ root: { className: 'mb-3' } }}>
                <div className="question-text">{question}</div>
                <div className="answer-buttons">
                    <Button
                        label={isUserAnswered ? "Answered" : "Mark as Answered"}
                        className={`trivia-button ${isUserAnswered ? "answer-button-correct" : "answer-button-default"}`}
                        icon={isUserAnswered ? "pi pi-check" : "pi pi-circle"}
                        onClick={() => toggleAnswered(
                            categoryKey,
                            index,
                            auth.currentUser.uid
                        )}
                    />
                    {hasPartner && (
                        <Button
                            label={isPartnerAnswered ? "Partner Answered" : "Mark Partner Answered"}
                            className={`trivia-button ${isPartnerAnswered ? "answer-button-partner" : "answer-button-default"}`}
                            icon={isPartnerAnswered ? "pi pi-check" : "pi pi-circle"}
                            onClick={() => toggleAnswered(
                                categoryKey,
                                index,
                                partnerId
                            )}
                            disabled={!partnerId}
                        />
                    )}
                </div>
            </Card>
        );
    };

    return (
        <div className="game-container">
            <div className="player-scores">
                <div className="score-badge user-score-badge">
                        <i className="pi pi-user"></i>
                    {currentUserName}: <span>{userScore}</span>
                </div>
                {hasPartner && (
                    <div className="score-badge partner-score-badge">
                            <i className="pi pi-users"></i>
                        {partnerName}: <span>{partnerScore}</span>
                    </div>
                )}
            </div>

            <Card className="category-section">
                <h2>Select Question Category</h2>
                <div className="categories-tabs">
                    {getAllAvailableCategories().map(category => {
                        const categoryKey = formatCategoryKey(category);
                        const hasQuestions = categoryHasQuestions(category);
                        const hasUserQuestions = !!questions.standard?.[categoryKey];
                        const hasPartnerQuestions = !!partnerQuestions.standard?.[categoryKey];

                        return (
                            <Button
                                key={category}
                                className={`category-tab ${activeCategory === category ? 'active' : ''}
                                ${!hasQuestions ? 'empty' : ''}
                                ${hasPartnerQuestions && !hasUserQuestions ? 'partner-category' : ''}
                                ${animationCategory === category ? 'animating' : ''}`}
                                onClick={() => handleCategoryClick(category)}
                                disabled={categoryLoading === category || animating}
                                severity={activeCategory === category ? "primary" : "secondary"}
                                outlined={!hasQuestions}
                            >
                                {category}
                                {categoryLoading === category ? (
                                    <i className="pi pi-spin pi-spinner ml-2"></i>
                                ) : !hasQuestions ? (
                                    <i className="pi pi-bolt ml-2"></i>
                                ) : hasPartnerQuestions && !hasUserQuestions ? (
                                    <i className="pi pi-users ml-2" title="From partner"></i>
                                ) : null}
                            </Button>
                        );
                    })}
                </div>
                <div className="mt-3">
                    <Button
                        className="random-category-button p-button-rounded"
                        icon="pi pi-shuffle"
                        onClick={handleRandomCategory}
                        disabled={animating}
                        label={animating ? "Selecting..." : "Random Category"}
                    />
                </div>
            </Card>

            <Card className="questions-panel">
                {categoryLoading === activeCategory && (
                    <div className="panel-loading-overlay">
                        <div className="loader-container">
                            <Hearts height="80" width="80" color="#f97316" ariaLabel="hearts-loading" visible={true} />
                            <p>
                                {loadingType === 'creating' 
                                    ? "Category is being created..." 
                                    : "Regenerating questions..."}
                            </p>
                        </div>
                    </div>
                )}
                <div className="questions-header">
                    <div className="questions-header-title">
                        <h2>{activeCategory} Questions</h2>
                        {(() => {
                            const categoryKey = formatCategoryKey(activeCategory);
                            const hasUserQuestions = !!questions.standard?.[categoryKey];
                            const hasPartnerQuestions = !!partnerQuestions.standard?.[categoryKey];

                            if (!hasUserQuestions && hasPartnerQuestions) {
                                return (
                                    <span className="category-creator-badge">
                                        <i className="pi pi-users"></i>
                                        Created by {partnerName}
                                    </span>
                                );
                            }
                            return null;
                        })()}
                    </div>
                    <div>
                        <div className="regenerate-container">
                            <Button
                                icon={categoryLoading === activeCategory ? "pi pi-spin pi-spinner" : "pi pi-reload"}
                                onClick={() => handleRegenerateCategory(activeCategory)}
                                disabled={categoryLoading === activeCategory || remainingRegenerations <= 0}
                                className="trivia-button"
                                tooltip={remainingRegenerations <= 0 ? "No regenerations left" : undefined}
                            />
                            <span 
                                className="regeneration-count" 
                                style={{ color: (remainingRegenerations || 0) <= 0 ? '#dc3545' : '#666' }}
                                onClick={() => console.log("Current remainingRegenerations displayed in UI:", remainingRegenerations)}
                            >
                                {remainingRegenerations || 0} left
                            </span>
                        </div>
                    </div>
                </div>
                <div className="questions-list">
                    {loading ? (
                        <div className="loading-indicator">
                            <Hearts height="80" width="80" color="#f97316" ariaLabel="hearts-loading" visible={true} />
                            <p>Loading questions...</p>
                        </div>
                    ) : (
                        <>
                            {(() => {
                                const categoryQuestions = getQuestionsForCategory(activeCategory);
                                const categoryKey = formatCategoryKey(activeCategory);
                                const hasUserQuestions = !!questions.standard?.[categoryKey];
                                const hasPartnerQuestions = !!partnerQuestions.standard?.[categoryKey];

                                if (categoryQuestions) {
                                    return (
                                        <>
                                            {!hasUserQuestions && hasPartnerQuestions && (
                                                <div className="partner-questions-notice">
                                                    <i className="pi pi-info-circle"></i>
                                                    <span>These questions were created by your partner. You can still answer them!</span>
                                                </div>
                                            )}
                                            {categoryQuestions.map((question, index) =>
                                                renderQuestionCard(question, index)
                                            )}
                                        </>
                                    );
                                } else {
                                    return (
                                        <div className="empty-state">
                                            <p>No questions available for this category yet.</p>
                                            <Button
                                                label="Generate Questions"
                                                onClick={() => handleGenerateCategory(activeCategory)}
                                                disabled={categoryLoading === activeCategory || remainingRegenerations <= 0}
                                                tooltip={remainingRegenerations <= 0 ? "No regenerations left" : undefined}
                                            />
                                        </div>
                                    );
                                }
                            })()}
                        </>
                    )}
                </div>
            </Card>

            {/* Custom dialog for category regeneration */}
            <Dialog
                header={`Regenerate ${currentCategory} Questions`}
                visible={showRegenerateDialog}
                className="frazza-dialog-sm"
                onHide={() => setShowRegenerateDialog(false)}
                footer={
                    <>
                        <Button 
                            label="Cancel" 
                            icon="pi pi-times" 
                            onClick={() => setShowRegenerateDialog(false)} 
                            className="p-button-text" 
                        />
                        <Button 
                            label="Regenerate" 
                            icon="pi pi-refresh" 
                            onClick={confirmRegeneration} 
                            autoFocus 
                            className="p-button-danger"
                        />
                    </>
                }
            >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                    <div className="frazza-alert-icon warning">
                        <i className="pi pi-exclamation-triangle"></i>
                    </div>
                    <div className="frazza-alert-content">
                        <p className="frazza-alert-message">
                            Are you sure you want to regenerate {currentCategory} questions? This will replace the questions in this category, but your overall score will be preserved.
                        </p>
                    </div>
                </div>
            </Dialog>

            {/* Dialog for when no regenerations are left */}
            <Dialog
                header="No Regenerations Left"
                visible={showNoRegenerationsDialog}
                className="frazza-dialog-sm"
                onHide={() => setShowNoRegenerationsDialog(false)}
                footer={
                    <Button 
                        label="OK" 
                        icon="pi pi-check" 
                        onClick={() => setShowNoRegenerationsDialog(false)} 
                        autoFocus
                    />
                }
            >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                    <div className="frazza-alert-icon danger">
                        <i className="pi pi-exclamation-circle"></i>
                    </div>
                    <div className="frazza-alert-content">
                        <p className="frazza-alert-message">
                            No more regeneration possible for {currentCategory} questions. Come back in 30 days when your regeneration count resets.
                        </p>
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

export default Questions;
